import { ActivityKind } from '@/enums/activity'
import i18n from '@/libs/i18n'
import { formatDayRange } from '@/libs/dayjs'
import { taskStatusLabel } from '@/libs/task'
import type { IActivity } from '@/types/Activity'
import type { IUserMin } from '@/types/User'

/*
 * WHAT HAPPENED, IN A SENTENCE — one line of a history (dashboard, project, task).
 *
 * The sentence names the change and leaves out who made it and which task it was: the row
 * shows the actor beside it and the task as a link of its own. Written as a noun phrase
 * ("Nova naloga", "Status: V delu → V pregledu") rather than a verb, because a Slovenian verb
 * would have to agree with the actor's gender, which the console does not know.
 *
 * `data` is read defensively: a key that is missing or of another type simply drops out of
 * the sentence instead of printing "undefined".
 */
const t = (key: string, values?: Record<string, unknown>): string =>
    values ? i18n.global.t(key, values) : i18n.global.t(key)

const text = (value: unknown): string => typeof value === 'string' ? value : ''

const personName = (value: unknown): string => {
    const person = value as IUserMin | null | undefined

    return person && typeof person === 'object' && person.name ? person.name : t('task.unassigned')
}

/** The label of a task field as `task_updated` names it. */
const FIELD_KEYS: Record<string, string> = {
    title:          'task.title',
    description:    'task.description',
    status:         'task.status',
    priority:       'task.priority',
    assignee:       'task.assignee',
    assignee_id:    'task.assignee',
    start_date:     'task.start_date',
    due_date:       'task.due_date',
    milestone:      'task.milestone',
    estimate_hours: 'task.estimate',
    progress:       'task.progress',
    key:            'project.key',
    name:           'project.name',
    lead:           'project.lead',
    lead_id:        'project.lead',
}

const fieldList = (value: unknown): string => {
    if (!Array.isArray(value)) return ''

    return value
        .filter((field): field is string => typeof field === 'string')
        .map((field) => FIELD_KEYS[field] ? t(FIELD_KEYS[field]).toLowerCase() : field)
        .join(', ')
}

const withDetail = (sentence: string, detail: string): string => detail ? `${sentence}: ${detail}` : sentence

export const activitySentence = (activity: IActivity): string => {
    const data = activity.data ?? {}

    switch (activity.kind) {
        case ActivityKind.TaskCreated:
            return t('activity.task_created')
        case ActivityKind.TaskUpdated:
            return withDetail(t('activity.task_updated'), fieldList(data.fields))
        case ActivityKind.StatusChanged:
            return t('activity.status_changed', { from: taskStatusLabel(text(data.from)), to: taskStatusLabel(text(data.to)) })
        case ActivityKind.Assigned:
            return t('activity.assigned', { from: personName(data.from), to: personName(data.to) })
        case ActivityKind.ScheduleChanged: {
            const from = formatDayRange(text(data.from_start) || null, text(data.from_due) || null) || t('task.no_dates')
            const to   = formatDayRange(text(data.to_start) || null, text(data.to_due) || null) || t('task.no_dates')
            const line = t('activity.schedule_changed', { from, to })
            const by   = text(data.cascade_from)

            return by ? `${line} ${t('activity.cascade_from', { key: by })}` : line
        }
        case ActivityKind.DependenciesChanged:
            return t('activity.dependencies_changed')
        case ActivityKind.CommentAdded:
            return t('activity.comment_added')
        case ActivityKind.AttachmentAdded:
            return withDetail(t('activity.attachment_added'), text(data.name) || text(data.original_name))
        case ActivityKind.AttachmentRemoved:
            return withDetail(t('activity.attachment_removed'), text(data.name) || text(data.original_name))
        case ActivityKind.TaskDeleted:
            return t('activity.task_deleted')
        case ActivityKind.ProjectCreated:
            return t('activity.project_created')
        case ActivityKind.ProjectUpdated:
            return withDetail(t('activity.project_updated'), fieldList(data.fields))
        case ActivityKind.MembersChanged:
            return t('activity.members_changed')
        default:
            return activity.kind
    }
}

/** The icon beside a history line. */
export const activityIcon = (kind: string): string => {
    switch (kind) {
        case ActivityKind.TaskCreated:         return 'i-lucide-plus'
        case ActivityKind.StatusChanged:       return 'i-lucide-circle-dot'
        case ActivityKind.Assigned:            return 'i-lucide-user-round-check'
        case ActivityKind.ScheduleChanged:     return 'i-lucide-calendar-range'
        case ActivityKind.DependenciesChanged: return 'i-lucide-link'
        case ActivityKind.CommentAdded:        return 'i-lucide-message-square'
        case ActivityKind.AttachmentAdded:     return 'i-lucide-paperclip'
        case ActivityKind.AttachmentRemoved:   return 'i-lucide-paperclip'
        case ActivityKind.TaskDeleted:         return 'i-lucide-trash-2'
        case ActivityKind.ProjectCreated:      return 'i-lucide-folder-plus'
        case ActivityKind.ProjectUpdated:      return 'i-lucide-folder-pen'
        case ActivityKind.MembersChanged:      return 'i-lucide-users'
        default:                               return 'i-lucide-pencil'
    }
}

/** Whether the task of a line can still be opened — a deleted task cannot. */
export const activityTaskOpenable = (activity: IActivity): boolean =>
    !!activity.task && activity.kind !== ActivityKind.TaskDeleted
